import { TaJson } from "ta-json";

import { MapStringTo, Nullable, NullableResultPromise } from "../base-types";
import { IExtendedContentHubClient } from "../clients/extended-client";
import { EXTENSION_DATA_KEYS } from "../constants/api";
import { IEntity } from "../contracts/base/entity";
import { IEntityLoadConfiguration } from "../contracts/querying/entity-load-configuration";
import { EntityQueryResult, IEntityQueryResult } from "../contracts/querying/entity-query-result";
import { EntitySearchAfterResult, IEntitySearchAfterResult } from "../contracts/querying/entity-search-after-result";
import { EntityIdQueryResult, IIdQueryResult } from "../contracts/querying/id-query-result";
import { MinimalSchema } from "../contracts/querying/minimal-schema";
import { Query } from "../contracts/querying/query";
import Guard from "../guard";
import { EntityCollectionResource } from "../models/entity-collection-resource";
import { QueryResultResource } from "../models/query-result-resource";

export class QueryingMapper {
    private readonly _client: IExtendedContentHubClient;

    constructor(client: IExtendedContentHubClient) {
        Guard.notNullOrUndefined(client);
        this._client = client;
    }

    async mapEntityQueryResultAsync(
        resource: QueryResultResource,
        query: Query,
        loadConfiguration: IEntityLoadConfiguration
    ): NullableResultPromise<IEntityQueryResult> {
        if (resource == null) return null;

        Guard.notNullOrUndefined(query, "query");

        const entities = await this.getEntitiesAsync(resource);
        const total = resource.totalItems || 0;
        const skip = query.skip || 0;
        const result = new EntityQueryResult(this._client, query, loadConfiguration, entities, total, skip);

        return result;
    }

    async mapEntityIdQueryResultAsync(
        resource: QueryResultResource,
        query: Query
    ): NullableResultPromise<IIdQueryResult> {
        if (resource == null) return null;

        Guard.notNullOrUndefined(query, "query");

        const ids = await this.getEntityIdsAsync(resource);
        const total = resource.totalItems || 0;
        const skip = query.skip || 0;
        const result = new EntityIdQueryResult(this._client, query, ids, total, skip);

        return result;
    }

    async mapEntitySearchAfterResult(resource: EntityCollectionResource): Promise<Nullable<IEntitySearchAfterResult>> {
        if (resource == null) {
            return null;
        }

        const entities = await this.getEntitiesForSearchAfterAsync(resource);
        const total = resource.totalItems || 0;
        const returnedItems = resource.returnedItems || 0;

        const result = new EntitySearchAfterResult(entities, total, returnedItems, resource.lastHitData);

        return result;
    }

    private static getMinimalSchemas(resource: QueryResultResource): MapStringTo<MinimalSchema> {
        const map: MapStringTo<MinimalSchema> = {};

        const minimalSchema = resource.minimalSchemaList || resource[EXTENSION_DATA_KEYS.minimalSchema];
        if (minimalSchema == null) {
            return map;
        }

        const schemaTokens = (
            minimalSchema instanceof Array
                ? minimalSchema
                : typeof minimalSchema === "string"
                  ? JSON.parse(minimalSchema)
                  : []
        ) as Array<MinimalSchema>;

        for (const token of schemaTokens) {
            const schema = TaJson.deserialize<MinimalSchema>(token, MinimalSchema);
            map[schema.definitionName] = schema;
        }

        return map;
    }

    private static getMinimalSchemasForSearchAfter(resource: EntityCollectionResource): MapStringTo<MinimalSchema> {
        const map: MapStringTo<MinimalSchema> = {};

        const minimalSchema = resource.minimalSchemaList || resource[EXTENSION_DATA_KEYS.minimalSchema];
        if (minimalSchema == null) {
            return map;
        }

        const schemaTokens = (
            minimalSchema instanceof Array
                ? minimalSchema
                : typeof minimalSchema === "string"
                  ? JSON.parse(minimalSchema)
                  : []
        ) as Array<MinimalSchema>;

        for (const token of schemaTokens) {
            const schema = TaJson.deserialize<MinimalSchema>(token, MinimalSchema);
            map[schema.definitionName] = schema;
        }

        return map;
    }

    private async getEntitiesAsync(resource: QueryResultResource): Promise<Array<IEntity>> {
        if (!resource.items || !resource.items.length) {
            return [];
        }

        const schemasMap = QueryingMapper.getMinimalSchemas(resource);
        const resources = resource.items.map(item => item.entity);
        const entities = await this._client.entityMapper.mapEntitiesAsync(resources, schemasMap);

        return entities;
    }

    private async getEntitiesForSearchAfterAsync(resource: EntityCollectionResource): Promise<Array<IEntity>> {
        if (!resource.items || !resource.items.length) {
            return [];
        }

        const schemasMap = QueryingMapper.getMinimalSchemasForSearchAfter(resource);
        const entities = await this._client.entityMapper.mapEntitiesAsync(resource.items, schemasMap);

        return entities;
    }

    private async getEntityIdsAsync(resource: QueryResultResource): Promise<Array<number>> {
        if (!resource.items || !resource.items.length) {
            return [];
        }

        const links = resource.items.map(item => item.link);
        const ids = await this._client.linkHelper.idsFromEntitiesAsync(links);

        return ids;
    }
}
